import { mm } from "@inovitas/infra3dsdk";
import {
  Checkbox,
  FormControl,
  Input,
  MenuItem,
  Select,
  SelectChangeEvent,
  TableCell,
  Typography,
} from "@mui/material";
import { Feature } from "geojson";
import React from "react";

import { defaultStore } from "shared/common/stores/defaultStore";
import { sidebarStore } from "shared/common/stores/sidebarStore";
import { manageKeyboardEvents } from "shared/common/utils/keyboardEvents/manageKeyboardEvents";
import getChangedFeaturesList from "shared/common/utils/layers/getChangedFeaturesList";
import { returnType } from "./cellFunctions/returnType";
import { returnValue } from "./cellFunctions/returnValue";
import { FeatureColumn } from "./FeatureListTable";

interface props {
  selectedTableRows: Feature;
  col: FeatureColumn;
  item: string;
  onChange: (value: string, item: Feature, columnName: string) => void;
}

const SingleFeatureCell = ({
  selectedTableRows,
  col,
  item,
  onChange,
}: props) => {
  const viewer = defaultStore((state) => state.viewer);
  const darkTheme = defaultStore((state) => state.darkTheme);

  const editPopover = sidebarStore((state) => state.editPopover);
  const changeFeatureTableAddedRows = sidebarStore(
    (state) => state.changeFeatureTableAddedRows
  );
  const enableEditFeature = sidebarStore((state) => state.enableEditFeature);

  const dateTimeRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\+\d{2}:\d{2}$/;
  const dateTimeRegex2 = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;

  //format date to same template
  const formatDateInput = (date: string) => {
    if (dateTimeRegex.test(date)) {
      return date.split("+")[0];
    } else if (dateTimeRegex2.test(date)) {
      return date;
    } else if (date.toString().length === 13 || date.toString().length === 14) {
      return new Date(date).toISOString().slice(0, 19);
    } else if (date.toString().length === 10) {
      return new Date(Number(date) * 1000).toISOString().slice(0, 19);
    } else {
      return "";
    }
  };

  //activate and deactivate keyboard events while writing on input fields
  const handleKeyboardEvents = (state: boolean) => {
    if (viewer) {
      manageKeyboardEvents(viewer, state);
      if (!state) {
        const layer = viewer.getScene().getLayerHandler().getActiveLayer();
        if (layer === null) return;
        changeFeatureTableAddedRows(getChangedFeaturesList(layer));
      }
    }
  };

  return (
    <TableCell sx={{ padding: "4px" }}>
      {(enableEditFeature || editPopover) && col.editable ? (
        col.datatype === mm.LAYERPROPERTYTYPE.ENUM ? (
          <FormControl
            classes={{
              root: darkTheme
                ? "outlindedFeatureSelectInputWhite"
                : "outlindedFeatureSelectInput",
            }}
            size="small"
            sx={{ m: 0 }}
          >
            <Select
              aria-labelledby="demo-radio-buttons-group-label"
              value={
                typeof item === "number" && col.values
                  ? col.values[item] || ""
                  : item || ""
              }
              name="enum-select"
              onChange={(event: SelectChangeEvent) => {
                onChange(event.target.value, selectedTableRows, col.key);
                handleKeyboardEvents(false);
              }}
              classes={{
                select: darkTheme
                  ? "editFeatureSelectInputWhite"
                  : "editFeatureSelectInput",
              }}
              sx={{ fontSize: "0.8rem", minHeight: "auto", padding: 0 }}
            >
              <MenuItem
                classes={{
                  root: darkTheme
                    ? "editFeatureSelectInputWhite"
                    : "editFeatureSelectInput",
                }}
                id="editFeatureSelectInputNone"
                value=""
                sx={{ fontSize: "0.8rem", minHeight: "auto" }}
              >
                -
              </MenuItem>
              {col.values &&
                col.values.map((val: string, index: number) => {
                  return (
                    <MenuItem
                      classes={{
                        root: darkTheme
                          ? "editFeatureSelectInputWhite"
                          : "editFeatureSelectInput",
                      }}
                      sx={{ fontSize: "0.8rem", minHeight: "auto" }}
                      key={index + "_" + val}
                      value={val}
                    >
                      {val}
                    </MenuItem>
                  );
                })}
            </Select>
          </FormControl>
        ) : col.datatype === mm.LAYERPROPERTYTYPE.BOOLEAN ? (
          <Checkbox
            size="small"
            classes={{
              root: darkTheme
                ? "editFeatureRadioButtonAccentWhite"
                : "editFeatureRadioButtonAccent",
              checked: darkTheme
                ? "editFeatureRadioButtonAccentWhite"
                : "editFeatureRadioButtonAccent",
            }}
            sx={{ padding: "0px" }}
            defaultChecked={Boolean(item)}
            onChange={(event, checked: boolean) => {
              onChange(String(checked), selectedTableRows, col.key);
              handleKeyboardEvents(false);
            }}
          />
        ) : col.datatype === mm.LAYERPROPERTYTYPE.DATE ? (
          <Input
            className={darkTheme ? "iconForInputDateWhite" : "iconForInputDate"}
            disableUnderline
            sx={{
              border: "none",
              fontSize: "12px",
              padding: "2px 4px",
              color: darkTheme ? "white" : "darkblue.main",
              backgroundColor: darkTheme ? "primary.light" : "grey.light",
            }}
            type={returnType(item, col.datatype)}
            defaultValue={item ? formatDateInput(item) : ""}
            onFocus={() => handleKeyboardEvents(true)}
            onBlur={(event) => {
              onChange(event.target.value, selectedTableRows, col.key);
              handleKeyboardEvents(false);
            }}
          />
        ) : (
          <Input
            className={darkTheme ? "iconForInputDateWhite" : "iconForInputDate"}
            disableUnderline
            sx={{
              border: "none",
              fontSize: "12px",
              padding: "2px 4px",
              color: darkTheme ? "white" : "darkblue.main",
              backgroundColor: darkTheme ? "primary.light" : "grey.light",
            }}
            type={returnType(item, col.datatype)}
            defaultValue={item}
            onFocus={() => handleKeyboardEvents(true)}
            onBlur={(event) => {
              onChange(event.target.value, selectedTableRows, col.key);
              handleKeyboardEvents(false);
            }}
          />
        )
      ) : (
        <Typography
          sx={{
            fontSize: "12px",
            color: darkTheme ? "white" : "darkblue.main",
          }}
        >
          {returnValue(item)}
        </Typography>
      )}
    </TableCell>
  );
};
export default SingleFeatureCell;
